import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { debounce } from '../../utils/debounce';
import { useI18n } from '../../i18n/I18nContext';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import './SearchableDropdown.css';

/**
 * Searchable dropdown with lazy loading: a `.ui-select` trigger, and a `.ui-menu` card
 * (portaled, spec §3.9) holding a search field and the list. Loads items in chunks instead
 * of all at once and filters locally while the caller's `onSearch` can fetch more.
 *
 * Keyboard: type to filter, Arrow keys move, Enter picks, Esc closes (and leaves a dialog
 * under it open).
 *
 * Naming (same rule as <Dropdown>): `ariaLabel`, a `<label htmlFor={id}>` or `labelledBy` (the id
 * of a visible label) names the trigger; the current value stays in its description, so a screen
 * reader says "Kurs, button, Rus tili" and never loses what is selected.
 */
const SearchableDropdown = ({
  items = [],
  selectedId,
  onChange,
  onSearch,
  getLabel = (item) => item.name || item.title,
  placeholder,
  disabled = false,
  loading = false,
  pageSize = 20,
  hasMore = false,
  onLoadMore,
  size = 'md',
  id,
  ariaLabel,
  labelledBy,
}) => {
  const { t } = useI18n();
  const uid = useId();
  const valueId = `${uid}-value`;
  const resolvedPlaceholder = placeholder ?? t('ui.selectOptionPlaceholder');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [active, setActive] = useState(-1);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pos = useAnchoredPopover(isOpen, setIsOpen, anchorRef, popRef, { matchWidth: true });

  // Persist the debounced search function so it isn't recreated on every render.
  // useMemo rebuilds the debounced function only when onSearch changes.
  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (onSearch) {
          onSearch(term);
        }
      }, 300),
    [onSearch]
  );

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setActive(0);
    debouncedSearch(term);
  };

  const displayItems = useMemo(() => {
    if (!searchTerm) return items.slice(0, pageSize);
    const needle = searchTerm.toLowerCase();
    return items.filter((item) => String(getLabel(item)).toLowerCase().includes(needle));
  }, [items, searchTerm, pageSize, getLabel]);

  // Handle scroll to load more items
  const handleScroll = (e) => {
    const element = e.target;
    if (
      element.scrollHeight - element.scrollTop < 100 &&
      hasMore &&
      !loading &&
      onLoadMore
    ) {
      onLoadMore();
    }
  };

  const close = useCallback(() => setIsOpen(false), []);

  const selectedItem = items.find((item) => item.id === selectedId);
  const selectedLabel = selectedItem ? getLabel(selectedItem) : resolvedPlaceholder;

  const openMenu = () => {
    if (disabled) return;
    const at = displayItems.findIndex((item) => item.id === selectedId);
    setActive(Math.max(0, at));
    setIsOpen(true);
  };

  const choose = (item) => {
    onChange(item.id);
    close();
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  // However the menu closes (pick, outside click, Esc), the next opening starts with an empty search.
  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (isOpen && positioned && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [isOpen, positioned]);

  useEffect(() => {
    if (!isOpen || active < 0 || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [isOpen, active]);

  const onSearchKeyDown = (e) => {
    const last = displayItems.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i >= last ? 0 : i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i <= 0 ? last : i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (displayItems[active]) choose(displayItems[active]); }
    else if (e.key === 'Tab') close();
  };

  const menu = isOpen ? (
    <div
      ref={popRef}
      className="ui-menu sdd-menu"
      style={pos
        ? { top: pos.top, left: pos.left, minWidth: pos.minWidth, zIndex: pos.zIndex }
        : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
    >
      <div className="ui-menu__search">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${uid}-list`}
          aria-activedescendant={active >= 0 && displayItems[active] ? `${uid}-opt-${active}` : undefined}
          placeholder={t('ui.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearch}
          onKeyDown={onSearchKeyDown}
          className="sdd-search"
          aria-label={t('ui.searchOptionsAria')}
          autoComplete="off"
        />
      </div>

      <div
        id={`${uid}-list`}
        role="listbox"
        className="ui-menu__list"
        onScroll={handleScroll}
        ref={listRef}
      >
        {displayItems.map((item, i) => {
          const isSelected = item.id === selectedId;
          return (
            <div
              key={item.id}
              id={`${uid}-opt-${i}`}
              role="option"
              aria-selected={isSelected}
              className={`ui-menu__item${isSelected ? ' is-selected' : ''}${i === active ? ' is-focused' : ''}`}
              onMouseMove={() => { if (active !== i) setActive(i); }}
              onClick={() => choose(item)}
            >
              <span>{getLabel(item)}</span>
              {isSelected && <Check className="ui-menu__check" aria-hidden="true" />}
            </div>
          );
        })}

        {displayItems.length === 0 && (
          <div className="ui-menu__empty">
            {loading ? t('ui.loading') : t('ui.noOptionsFound')}
          </div>
        )}

        {hasMore && !loading && (
          <button type="button" className="ui-menu__more" onClick={onLoadMore}>
            {t('ui.loadMoreItems')}
          </button>
        )}

        {loading && displayItems.length > 0 && (
          <div className="ui-menu__empty">{t('ui.loadingMore')}</div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="searchable-dropdown sdd" ref={anchorRef} data-disabled={disabled || undefined}>
      <button
        ref={triggerRef}
        id={id}
        className={`sdd-trigger ui-select${size === 'sm' ? ' ui-select--sm' : ''}${selectedItem ? '' : ' is-placeholder'}`}
        onClick={() => (isOpen ? close() : openMenu())}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : labelledBy}
        aria-describedby={selectedItem && (ariaLabel || labelledBy || id) ? valueId : undefined}
      >
        <span id={valueId} className="ui-select__label">{selectedLabel}</span>
        <ChevronDown className="ui-select__chevron" aria-hidden="true" />
      </button>
      {menu && createPortal(menu, popoverRoot(anchorRef.current))}
    </div>
  );
};

export default SearchableDropdown;
